import { initSentry } from './lib/sentry';
// Must run before ReactDOM.createRoot so the SDK is ready for any bootstrap errors
initSentry();

import { initConsoleCapture } from './lib/console-capture';
initConsoleCapture();

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Detect if running in Electron (file:// protocol or electronAPI available)
const isElectron = window.location.protocol === 'file:' || !!(window as any).electronAPI;

// Use HashRouter for Electron (file:// doesn't support History API)
const Router = isElectron ? HashRouter : BrowserRouter;
import './index.css';

import { ThemeProvider } from './components/theme-provider';
import { ThemePresetProvider } from './components/theme-preset-provider';
import { AuthProvider } from './hooks/useAuth';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { InactivityProvider } from './contexts/InactivityContext';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { OnboardingTourProvider } from './components/onboarding/OnboardingTour';
import { ThemedRootLayout } from './components/layout/ThemedRootLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppErrorBoundary } from './components/SentryErrorBoundary';
import Login from './pages/Login';
import TwoFactorVerify from './pages/TwoFactorVerify';
import TwoFactorSetupRequired from './pages/TwoFactorSetupRequired';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import MorningBrief from './pages/MorningBrief';
import Sell from './pages/sell';
import KDS from './pages/KDS';
import Customers from './pages/Customers';
import Settings from './pages/Settings';
import Team from './pages/Team';
import AuditLogs from './pages/AuditLogs';
import Reports from './pages/Reports';
import CloseRegister from './pages/CloseRegister';
import Kiosk from './pages/Kiosk';
import KioskSetup from './pages/KioskSetup';
import CustomerKioskLogin from './pages/CustomerKioskLogin';
import CustomerKiosk from './pages/CustomerKiosk';
import BackOffice from './pages/BackOffice';
import StockControl from './pages/StockControl';
import Standardization from './pages/Standardization';
// Promotions is nested inside Products, not a standalone route
import KitchenStations from './pages/KitchenStations';
import FloorPlan from './pages/FloorPlan';
import Utilities from './pages/Utilities';
import SettleRegister from './pages/SettleRegister';
import Approvals from './pages/Approvals';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ForgotPassword from './pages/ForgotPassword';
import AdminAccess from './pages/AdminAccess';
import Onboarding from './pages/Onboarding';
import Plans from './pages/Plans';
import CashierCount from './pages/CashierCount';
import ShortCodeRedirect from './pages/ShortCodeRedirect';
import RequestCount from './pages/RequestCount';
import ApproveCountRequest from './pages/ApproveCountRequest';
import DenyCountRequest from './pages/DenyCountRequest';

// Network / blocked-request errors (offline, CORS, edge rate-limit) — retrying
// these immediately just amplifies the flood and can trip Supabase's rate
// limiter, so we skip retries for them.
function isNetworkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return /Load failed|Failed to fetch|NetworkError|network error|access control|ERR_NETWORK|fetch failed/i.test(msg);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        if (isNetworkError(error)) return false;
        return failureCount < 1;
      },
      // Exponential backoff so a wave of failures doesn't hammer the API.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    },
    mutations: {
      retry: (failureCount, error) => {
        if (isNetworkError(error)) return false;
        return failureCount < 1;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    },
  },
});

function LegacyProductsRedirect() {
  const location = useLocation();
  const search = location.search || '';
  const hash = location.hash || '';
  const path = location.pathname.replace(/\/+$/, '');

  const redirectBase = (() => {
    if (path === '/products/promotions/new') return '/stock-control/promotions/new';
    if (path.startsWith('/products/promotions/edit/')) {
      return path.replace('/products/promotions/edit/', '/stock-control/promotions/edit/');
    }
    if (path === '/products/categories') return '/stock-control/categories';
    if (path === '/products/promotions') return '/stock-control/promotions';
    if (path === '/products/modifiers') return '/stock-control/modifiers';
    if (path === '/products/pricing') return '/stock-control/pricing';

    const params = new URLSearchParams(search);
    if (params.has('edit') || params.has('duplicate') || params.has('create') || params.has('import') || params.has('posImport')) {
      return '/stock-control/manage';
    }

    return '/stock-control/overview';
  })();

  return <Navigate to={`${redirectBase}${search}${hash}`} replace />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
    <ThemeProvider defaultTheme="system" storageKey="blazepos-theme">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemePresetProvider>
            <TooltipProvider>
            <OnboardingTourProvider>
            <ImpersonationProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/verify-2fa" element={<TwoFactorVerify />} />
              <Route path="/setup-2fa-required" element={<TwoFactorSetupRequired />} />
              <Route path="/register" element={<Register />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/admin-access" element={<AdminAccess />} />
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              } />
              <Route path="/plans" element={
                <ProtectedRoute>
                  <Plans />
                </ProtectedRoute>
              } />

              {/* Protected routes wrapped in themed root layout to prevent re-mounting */}
              <Route element={
                <ProtectedRoute>
                  <InactivityProvider>
                    <ThemedRootLayout />
                  </InactivityProvider>
                </ProtectedRoute>
              }>
                <Route 
                  path="/dashboard" 
                  element={<Dashboard />} 
                />
                <Route 
                  path="/sell/*" 
                  element={
                    <ProtectedRoute requiredPermission="pos.access" requiredModule="pos">
                      <Sell />
                    </ProtectedRoute>
                  } 
                />
                {/* Backward compatibility redirects */}
                <Route path="/pos" element={<Navigate to="/sell" replace />} />
                <Route path="/orders" element={<Navigate to="/sell/sales-history" replace />} />
                <Route
                  path="/kds"
                  element={
                    <ProtectedRoute requiredPermission="orders.view" requiredModule="kds">
                      <KDS />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kitchen-stations"
                  element={
                    <ProtectedRoute requiredPermission="settings.view" requiredModule="kdsStations">
                      <KitchenStations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/floor-plan"
                  element={
                    <ProtectedRoute requiredPermission="pos.access" requiredModule="floorPlan">
                      <FloorPlan />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products/*" 
                  element={
                    <ProtectedRoute requiredPermission={['menu.view', 'inventory.view']} requiredModule={['products', 'inventory']}>
                      <LegacyProductsRedirect />
                    </ProtectedRoute>
                  } 
                />
                {/* Redirect old /menu route to new location */}
                <Route
                  path="/menu"
                  element={<Navigate to="/stock-control/overview" replace />}
                />
                {/* Redirect old /inventory route to new location */}
                <Route
                  path="/inventory"
                  element={<Navigate to="/stock-control/overview" replace />}
                />
                <Route 
                  path="/stock-control/*" 
                  element={
                    <ProtectedRoute requiredPermission={['inventory.view', 'menu.view']} requiredModule={['inventory', 'products']}>
                      <StockControl />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/standardization/*" 
                  element={
                    <ProtectedRoute requiredPermission="menu.view" requiredModule="standardization">
                      <Standardization />
                    </ProtectedRoute>
                  } 
                />
                {/* Promotions is nested under /products/promotions/* */}
                <Route 
                  path="/customers" 
                  element={
                    <ProtectedRoute requiredPermission="customers.view" requiredModule="customers">
                      <Customers />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/settings" 
                  element={
                    <ProtectedRoute requiredPermission="settings.view">
                      <Settings />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/team" 
                  element={
                    <ProtectedRoute requiredPermission="team.view" requiredModule="team">
                      <Team />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/audit-logs" 
                  element={
                    <ProtectedRoute requiredPermission="audit.view" requiredModule="auditLogs">
                      <AuditLogs />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/reports/*"
                  element={
                    <ProtectedRoute requiredPermission="reports.view_sales" requiredModule="reports">
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/morning-brief"
                  element={
                    <ProtectedRoute requiredPermission="reports.view_sales" requiredModule="reports">
                      <MorningBrief />
                    </ProtectedRoute>
                  }
                />
                {/* Redirects for old report URLs */}
                <Route path="/analytics" element={<Navigate to="/reports/dashboard" replace />} />
                <Route path="/payment-reports" element={<Navigate to="/reports/payments" replace />} />
                <Route path="/register-closures" element={<Navigate to="/reports/register-closures" replace />} />
                <Route
                  path="/close-register/:sessionId" 
                  element={
                    <ProtectedRoute requiredPermission="cash.manage" requiredModule="settleRegisters">
                      <CloseRegister />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/settle-register" 
                  element={
                    <ProtectedRoute requiredPermission="cash.manage" requiredModule="settleRegisters">
                      <SettleRegister />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/settle-register/:sessionId" 
                  element={
                    <ProtectedRoute requiredPermission="cash.manage" requiredModule="settleRegisters">
                      <SettleRegister />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/command-center/*"
                  element={
                    <ProtectedRoute requiredPermission="backoffice.access" requiredModule="commandCenter">
                      <BackOffice />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/approvals"
                  element={
                    <ProtectedRoute>
                      <Approvals />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/utilities" 
                  element={
                    <ProtectedRoute requiredRole="superadmin" requiredModule="utilities">
                      <Utilities />
                    </ProtectedRoute>
                  } 
                />
              </Route>
              
              {/* Cashier Stock Count - Shareable, no auth required */}
              <Route path="/cashier-count/:token" element={<CashierCount />} />
              <Route path="/c/:code" element={<ShortCodeRedirect />} />
              <Route path="/request-count" element={<RequestCount />} />
              <Route path="/approve-count/:token" element={<ApproveCountRequest />} />
              <Route path="/deny-count/:token" element={<DenyCountRequest />} />

              {/* Kiosk - No authentication required for self-service */}
              <Route path="/kiosk" element={<Kiosk />} />
              <Route path="/kiosk/setup" element={<KioskSetup />} />
              
              {/* Customer Kiosk - Wallet-based self-service */}
              <Route path="/customer-kiosk" element={<CustomerKioskLogin />} />
              <Route path="/customer-kiosk/order" element={<CustomerKiosk />} />
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Router>
        </ImpersonationProvider>
            </OnboardingTourProvider>
            </TooltipProvider>
          <Toaster />
          </ThemePresetProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
